
import { useState, useEffect, useRef } from 'react';

export default function PhilosophySection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="py-20 bg-gradient-to-b from-gray-900 to-black relative overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="/assets/208d6dbfa7d327bdfb6285a45ac93173.png"
          alt="Creative Philosophy Background"
          className="w-full h-full object-cover object-top opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/90"></div>
      </div>

      {/* Floating Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-10 w-20 h-20 bg-orange-500/20 rounded-full blur-xl animate-float"></div>
        <div className="absolute top-1/2 right-20 w-32 h-32 bg-red-500/20 rounded-full blur-xl animate-float-delayed"></div>
        <div className="absolute bottom-1/4 left-1/3 w-16 h-16 bg-yellow-500/20 rounded-full blur-xl animate-float-slow"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className={`text-center transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          {/* Quote Block */}
          <div className="mb-12">
            <div className="relative inline-block">
              <i className="ri-double-quotes-l text-6xl text-orange-500/30 absolute -top-4 -left-8"></i>
              <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                <span className="bg-gradient-to-r from-orange-400 via-red-500 to-pink-500 bg-clip-text text-transparent">
                  Designs are in our blood.
                </span>
                <br />
                <span className="text-white">We bleed freshness.</span>
              </h2>
              <i className="ri-double-quotes-r text-6xl text-orange-500/30 absolute -bottom-4 -right-8"></i>
            </div>
          </div>

          {/* Philosophy Content */}
          <div className={`max-w-4xl mx-auto transition-all duration-1000 delay-300 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <p className="text-2xl md:text-3xl text-gray-200 mb-8 leading-relaxed font-light">
              Since <strong className="text-orange-400">2014</strong>, Hash Adz has been an ever-evolving force in 
              <strong className="text-orange-400"> design</strong>, 
              <strong className="text-orange-400"> storytelling</strong>, and 
              <strong className="text-orange-400"> digital strategy</strong>.
            </p>
          </div>

          {/* Creative Elements Grid */}
          <div className={`grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 transition-all duration-1000 delay-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            {[
              { icon: 'ri-palette-line', label: 'Creative Design', color: 'from-orange-500 to-red-500' },
              { icon: 'ri-lightbulb-line', label: 'Innovation', color: 'from-red-500 to-pink-500' },
              { icon: 'ri-heart-pulse-line', label: 'Passion', color: 'from-pink-500 to-purple-500' },
              { icon: 'ri-rocket-line', label: 'Growth', color: 'from-purple-500 to-orange-500' }
            ].map((item, index) => (
              <div
                key={index}
                className={`group p-6 rounded-2xl bg-gray-800/30 backdrop-blur-sm border border-orange-500/20 hover:border-orange-500/50 transition-all duration-500 hover:transform hover:scale-110 animate-fade-in-up`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r ${item.color} flex items-center justify-center group-hover:animate-pulse`}>
                  <i className={`${item.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-white font-semibold text-lg">{item.label}</h3>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-30px) rotate(-180deg); }
        }
        @keyframes float-slow {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(90deg); }
        }
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 8s ease-in-out infinite;
        }
        .animate-float-slow {
          animation: float-slow 10s ease-in-out infinite;
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
          opacity: 0;
        }
      `}</style>
    </section>
  );
}
